import { useState, useEffect, useCallback } from 'react';

/**
 * 倒计时 Hook
 * @param initialSeconds 初始秒数
 * @param autoStart 是否自动开始
 * @returns { seconds, isActive, start, reset }
 */
export function useCountdown(initialSeconds: number = 300, autoStart: boolean = false) {
  const [seconds, setSeconds] = useState(autoStart ? initialSeconds : 0);
  const [isActive, setIsActive] = useState(autoStart);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isActive && seconds > 0) {
      interval = setInterval(() => {
        setSeconds(prev => {
          if (prev <= 1) {
            setIsActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, seconds]);

  /**
   * 开始倒计时
   */
  const start = useCallback(
    (customSeconds?: number) => {
      setSeconds(customSeconds ?? initialSeconds);
      setIsActive(true);
    },
    [initialSeconds]
  );

  /**
   * 重置倒计时
   */
  const reset = useCallback(() => {
    setSeconds(0);
    setIsActive(false);
  }, []);

  /**
   * 格式化显示时间 (mm:ss)
   */
  const formatTime = useCallback((secs: number) => {
    const minutes = Math.floor(secs / 60);
    const remainingSeconds = secs % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  }, []);

  return {
    seconds,
    isActive,
    start,
    reset,
    formattedTime: formatTime(seconds),
    canResend: !isActive && seconds === 0,
  };
}

export default useCountdown;
